import React, { useRef, useState, useEffect } from 'react';
import {
    Button,
    Typography,
    Box,
    TextField,
    CircularProgress
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';

export const FileManager = ({
    mode,
    filesList,
    error,
    downloadKey,
    setDownloadKey,
    uploadKey,
    setUploadKey,
    uploadFile,
    setUploadFile,
    fetchFileList,
    downloadFileFromS3,
    uploadFileToS3,
    deleteFileFromS3,
    refreshTrigger = 0
}) => {
    const fileInputRef = useRef(null);
    const hasInitializedRef = useRef(false);
    const [isListingFiles, setIsListingFiles] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [localFilesList, setLocalFilesList] = useState(filesList);
    const [selectedFileName, setSelectedFileName] = useState('');

    // Add useEffect to update local state when filesList changes
    useEffect(() => {
        setLocalFilesList(filesList);
        console.log('FilesList updated:', filesList);
    }, [filesList]);

    // Trigger fetchFileList when component mounts (only once)
    useEffect(() => {
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            const handleFetchFileList = async () => {
                setIsListingFiles(true);
                try {
                    await fetchFileList();
                    console.log('Fetch file list completed on mount');
                } catch (err) {
                    console.error('Error fetching file list on mount:', err);
                } finally {
                    setIsListingFiles(false);
                }
            };
            handleFetchFileList();
        }
    }, [fetchFileList]); // Include fetchFileList but guard with ref

    // Trigger fetchFileList when refreshTrigger changes
    useEffect(() => {
        if (refreshTrigger > 0) {
            const handleRefresh = async () => {
                setIsListingFiles(true);
                try {
                    await fetchFileList();
                    console.log('Fetch file list completed on refresh trigger');
                } catch (err) {
                    console.error('Error fetching file list on refresh trigger:', err);
                } finally {
                    setIsListingFiles(false);
                }
            };
            handleRefresh();
        }
    }, [refreshTrigger, fetchFileList]);

    // Function to transform flat file list into tree structure
    const transformFilesToTreeData = (files) => {
        const treeData = [];
        const folderMap = new Map();

        // First pass: Create folders
        files.forEach(file => {
            const parts = file.split('/');

            // If it's a folder or has nested path
            if (parts.length > 1) {
                let currentPath = '';
                parts.slice(0, -1).forEach((folder, index) => {
                    currentPath += (index > 0 ? '/' : '') + folder;

                    if (!folderMap.has(currentPath)) {
                        const folderItem = {
                            id: currentPath,
                            label: folder,
                            children: []
                        };
                        folderMap.set(currentPath, folderItem);

                        // Add to parent folder or root
                        const parentPath = parts.slice(0, index).join('/');
                        if (parentPath && folderMap.has(parentPath)) {
                            folderMap.get(parentPath).children.push(folderItem);
                        } else {
                            treeData.push(folderItem);
                        }
                    }
                });
            }
        });

        // Second pass: Add files to their respective folders
        files.forEach(file => {
            const parts = file.split('/');

            if (parts.length > 1) {
                const folderPath = parts.slice(0, -1).join('/');
                const parentFolder = folderMap.get(folderPath);

                if (parentFolder) {
                    parentFolder.children.push({
                        id: file,
                        label: parts[parts.length - 1]
                    });
                }
            } else {
                // Root level files
                treeData.push({
                    id: file,
                    label: file
                });
            }
        });
        return treeData;
    };

    const handleTreeItemSelection = (event, itemId) => {
        // Find the item in the tree data
        const findItemById = (items) => {
            for (const item of items) {
                if (item.id === itemId) return item;
                if (item.children) {
                    const found = findItemById(item.children);
                    if (found) return found;
                }
            }
            return null;
        };

        const selectedItem = findItemById(transformFilesToTreeData(localFilesList.files));

        // Check if the item has children (is a folder)
        if (selectedItem && selectedItem.children && selectedItem.children.length > 0) {
            // If it's a folder, ensure it ends with a slash
            setDownloadKey(itemId.endsWith('/') ? itemId : `${itemId}/`);
        } else {
            // If it's a file, set the full path
            setDownloadKey(itemId);
        }
    };

    const handleFetchFileList = async () => {
        setIsListingFiles(true);
        try {
            await fetchFileList();
            // Optional: Add any additional logging or state management
            console.log('Fetch file list completed');
        } catch (err) {
            console.error('Error fetching file list:', err);
        } finally {
            setIsListingFiles(false);
        }
    };

    const handleUploadFileToS3 = async () => {
        setIsUploading(true);
        try {
            await uploadFileToS3();
        } catch (err) {
            console.error('Error uploading file:', err);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadFileFromS3 = async () => {
        setIsDownloading(true);
        try {
            await downloadFileFromS3();
        } catch (err) {
            console.error('Error downloading file:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDeleteFileFromS3 = async () => {
        if (!downloadKey) {
            alert("No file selected for deletion.");
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${downloadKey}?`)) {
            setIsDeleting(true);
            try {
                await deleteFileFromS3();
                await fetchFileList(); // Refresh the file list after deletion
            } catch (err) {
                console.error('Error deleting file:', err);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <Box
            sx={{
                padding: '1em',
                border: '1px solid #ccc',
                borderRadius: '15px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: mode === 'light' ? '#f9f9f9' : '#424242',
                color: mode === 'light' ? '#000' : '#fff',
                maxWidth: '1800px',
                margin: '0 auto'
            }}
        >
            <Typography variant="h2" sx={{ fontSize: '1.5rem', marginBottom: '1em' }}>
                File Manager
            </Typography>

            {/* Main Container with two columns */}
            <Box sx={{ display: 'flex', gap: '2em' }}>
                {/* Left Column - File List (2/3 width) */}
                <Box sx={{ flex: '0 0 calc(66.666% - 1em)', minWidth: 0 }}>
                    {/* File List Section - Separate Box */}
                    <Box sx={{
                        padding: '1em',
                        border: '1px solid #ccc',
                        borderRadius: '10px',
                        backgroundColor: mode === 'light' ? '#fff' : '#616161',
                        color: mode === 'light' ? '#000' : '#fff'
                    }}>
                        <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1em' }}>
                            List files
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleFetchFileList}
                            disabled={isListingFiles}
                            sx={{ marginBottom: '1em' }}
                        >
                            {isListingFiles ? <CircularProgress size={24} /> : 'List All Files'}
                        </Button>

                        {localFilesList && localFilesList.files && (
                            <Box
                                key={JSON.stringify(localFilesList.files)} // Force re-render
                                sx={{
                                    backgroundColor: mode === 'light' ? '#f5f5f5' : '#505050',
                                    color: mode === 'light' ? '#000' : '#fff',
                                    padding: '1em',
                                    borderRadius: '5px',
                                    maxHeight: '500px',
                                    overflowY: 'auto'
                                }}
                            >
                                <RichTreeView
                                    items={transformFilesToTreeData(localFilesList.files)}
                                    onItemSelectionToggle={handleTreeItemSelection}
                                    sx={{
                                        color: mode === 'light' ? '#000' : '#fff',
                                        '& .MuiTreeItem-content': {
                                            color: mode === 'light' ? '#000' : '#fff'
                                        }
                                    }}
                                />
                            </Box>
                        )}

                        {error && (
                            <Typography
                                color="error"
                                sx={{ color: mode === 'light' ? 'red' : '#ff5252' }}
                            >
                                Error: {JSON.stringify(error)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Right Column - Upload and Download (1/3 width) */}
                <Box sx={{ flex: '0 0 calc(33.333% - 1em)', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2em' }}>
                    {/* Upload File Section - Separate Box */}
                    <Box sx={{
                        padding: '1em',
                        border: '1px solid #ccc',
                        borderRadius: '10px',
                        backgroundColor: mode === 'light' ? '#fff' : '#616161',
                        color: mode === 'light' ? '#000' : '#fff'
                    }}>
                        <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1em' }}>
                            Upload file
                        </Typography>
                        <Typography variant="body2" sx={{ marginBottom: '1em', fontSize: '0.85rem' }}>
                            Select a file using the choose file button, then put the destination file name in the text box.
                            To upload to a subfolder, simply put it in the text box as 'subfolder/filename.ext'
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
                            <Button
                                variant="contained"
                                component="label"
                                color="primary"
                                sx={{ height: '40px' }}
                            >
                                Choose File
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        setUploadFile(file);
                                        setSelectedFileName(file ? file.name : '');
                                    }}
                                    hidden
                                />
                            </Button>
                            {selectedFileName && (
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', wordBreak: 'break-word' }}>
                                    Selected: {selectedFileName}
                                </Typography>
                            )}

                            <TextField
                                type="text"
                                placeholder="Enter upload key"
                                value={uploadKey}
                                onChange={(e) => setUploadKey(e.target.value)}
                                size="small"
                                sx={{
                                    '& .MuiInputBase-root': {
                                        backgroundColor: mode === 'light' ? '#f5f5f5' : '#505050',
                                        color: mode === 'light' ? '#000' : '#fff'
                                    }
                                }}
                            />

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUploadFileToS3}
                                disabled={isUploading}
                                sx={{ height: '40px' }}
                            >
                                {isUploading ? <CircularProgress size={24} /> : 'Upload'}
                            </Button>
                        </Box>
                    </Box>

                    {/* Download File Section - Separate Box */}
                    <Box sx={{
                        padding: '1em',
                        border: '1px solid #ccc',
                        borderRadius: '10px',
                        backgroundColor: mode === 'light' ? '#fff' : '#616161',
                        color: mode === 'light' ? '#000' : '#fff'
                    }}>
                        <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1em' }}>
                            Download file
                        </Typography>
                        <Typography variant="body2" sx={{ marginBottom: '1em', fontSize: '0.85rem' }}>
                            To download a folder, specify the folder name, followed by the slash '/'.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
                            <TextField
                                type="text"
                                value={downloadKey}
                                onChange={(e) => setDownloadKey(e.target.value)}
                                placeholder="Enter file/folder name"
                                size="small"
                                sx={{
                                    '& .MuiInputBase-root': {
                                        backgroundColor: mode === 'light' ? '#f5f5f5' : '#505050',
                                        color: mode === 'light' ? '#000' : '#fff'
                                    }
                                }}
                            />

                            <Box sx={{ display: 'flex', gap: '0.5em' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleDownloadFileFromS3}
                                    disabled={isDownloading}
                                    sx={{ height: '40px', flex: 1 }}
                                >
                                    {isDownloading ? <CircularProgress size={24} /> : <CloudDownloadIcon />}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleDeleteFileFromS3}
                                    disabled={isDownloading || isDeleting || !downloadKey}
                                    sx={{ height: '40px', flex: 1 }}
                                >
                                    <DeleteForeverIcon />
                                </Button>
                            </Box>
                        </Box>

                        {error && (
                            <Typography
                                color="error"
                                sx={{ color: mode === 'light' ? 'red' : '#ff5252', marginTop: '1em', fontSize: '0.85rem' }}
                            >
                                Error: {JSON.stringify(error)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};